// 引入 React 运行时与组件状态 Hook。
import React, { useContext, useEffect, useState } from "react";
// 引入 className 合并工具。
import cx from "classnames";
// 引入 HTML 清洗工具，用于把评论 HTML 转成安全内容。
import sanitizeHtml from "sanitize-html";
// 引入国际化翻译 Hook。
import { useTranslations } from "next-intl";

// 引入页面路径常量，用于跳转帖子详情页。
import { pagePathUrl } from "@/constants/url";
// 引入 HTML 实体解码工具，用于处理接口返回的转义字符。
import { decodeHtmlEntities } from "@/utils/html";
// 引入消息同款时间格式化工具，保持“我的评论”和消息列表时间规则一致。
import { formatMessageDateByDayJs } from "@/utils/formatMessageDateByDayJs";
// 引入表情包接口，用于把表情 code 转换成图片展示。
import { getEmojiList } from "@/api/common";
// 引入评论跳转前置状态检查接口。
import { checkReplyRedirectStatus, type IUserReplyItem } from "@/api/userReplies";
// 引入评论内容工具，用于拉平表情包数据与替换表情 code。
import { flatEmojies, replaceEmojiCodeToImg } from "@/utils/thread";
// 引入 Toast 组件，用于引用评论删除时提示用户。
import Toast from "@/components/Toast";
// 引入全局上下文与命名空间，用于评论点击埋点。
import { StoreContext, namespace } from "@/context";

// 引入当前组件样式。
import styles from "./index.module.scss";
// 引入通用图片组件与图片类型枚举，用于展示评论图片。
import CommonImage, { ImageType } from "@/components/CommonImage";
// 引入评论骨架项，用于首次加载时占位。
import { SkeletonReplyItem } from "../SkeletonUserPostList";

// 引入表情包类型。
import type { IEmoji } from "@/api/common";

// 从一张 HTML 中提取第一张普通图片地址。
const extractFirstImageSrc = (html?: string): string => {
  // 兜底空字符串，避免正则处理 undefined。
  const raw = html || "";
  // 匹配第一张 img 标签上的 src。
  const match = raw.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/i);
  // 没有图片 src 时返回空字符串。
  if (!match?.[1]) return "";
  // 对图片地址做实体解码并去掉首尾空格。
  return decodeHtmlEntities(match[1]).trim();
};

// HTML 或富文本内容转成纯文本。
const sanitizeText = (text?: string): string => {
  // 兜底空字符串，避免清洗工具处理 undefined。
  const raw = text || "";
  // 清洗所有标签后再做实体解码。
  return decodeHtmlEntities(
    // 禁止所有 HTML 标签和属性，只保留文本内容。
    sanitizeHtml(raw, {
      // 不允许任何标签透出。
      allowedTags: [],
      // 不允许任何属性透出。
      allowedAttributes: {},
    })
  )
    // 压缩连续空白，避免 HTML 换行影响预览文本。
    .replace(/\s+/g, " ")
    // 去掉首尾空格。
    .trim();
};

// 将评论内容转换成可安全注入的预览 HTML。
// emojies 作为参数传入而非闭包捕获，使函数保持纯函数特性，引用稳定，避免随组件渲染重建。
const renderPreviewHtml = (text?: string, emojies: IEmoji[] = []): { __html: string } => {
  // 普通图片单独展示，因此预览文本里先移除 img 标签。
  const withoutContentImages = (text || "").replace(/<img[^>]*>/gi, "");
  // 按详情页评论逻辑把表情 code 替换成表情图片。
  const emojiHtml = replaceEmojiCodeToImg(withoutContentImages, emojies, styles);

  // 返回 React dangerouslySetInnerHTML 需要的对象结构。
  return {
    // 只允许表情图片标签透出，避免富文本注入风险。
    __html: sanitizeHtml(emojiHtml, {
      // 预览里只保留 img 标签。
      allowedTags: ["img"],
      // img 只保留展示所需属性。
      allowedAttributes: {
        // 允许表情图片地址、样式类名和兜底 alt。
        img: ["src", "class", "alt"],
      },
    })
      // 压缩空白，避免预览撑开布局。
      .replace(/\s+/g, " ")
      // 去掉首尾空格。
      .trim(),
  };
};

// 定义"我的评论"列表组件入参。
interface UserReplyListProps {
  // 列表数据，由上层分页逻辑传入。
  data: IUserReplyItem[];
  // 当前列表加载状态，用于展示骨架或分页 loading。
  loading?: boolean;
  // 是否还有更多分页数据。
  hasMore?: boolean;
  // 无更多分页数据时的底部文案。
  noMoreText?: string;
}

// 定义 PC 端用户中心“我的评论”列表组件。
const UserReplyList: React.FC<UserReplyListProps> = ({ data, loading = false, hasMore = true, noMoreText }) => {
  // 获取国际化翻译函数。
  const t = useTranslations();
  // 读取全局状态。
  const { state } = useContext(StoreContext);
  // 取埋点对象。
  const { sensors } = state[namespace.app];
  // 解构埋点方法和事件映射。
  const { track, TRACK_EVENTS } = sensors || {};
  // 保存接口拉取并拉平后的表情包列表。
  const [emojies, setEmojies] = useState<IEmoji[]>([]);
  // 记录正在校验跳转状态的评论，避免重复点击触发多次请求。
  const [checkingPid, setCheckingPid] = useState<number | null>(null);

  // 组件挂载时加载表情包数据。
  useEffect(() => {
    // 请求表情包接口。
    getEmojiList().then((res) => {
      // 将分组表情包拉平成详情页评论同款结构。
      setEmojies(flatEmojies(res));
    });
  }, []);

  // 上报评论点击进入详情结果。
  const reportReplyEntryClick = (item: IUserReplyItem, isSuccess: boolean, failReason = "") => {
    track?.(TRACK_EVENTS?.[namespace.home]?.threadEntryClick?.name, {
      page_name: "MyProfilePage",
      platform_type: "pc",
      position: "Feed",
      thread_name: sanitizeText(item.thread_title),
      thread_tid: item.tid,
      account_slug: item.threadAuthorId ?? item.author?.id,
      list_name: t("user_center_pc_my_comments"),
      is_success: isSuccess,
      fail_reason: failReason,
    }, namespace.home);
  };

  // 点击评论前先请求后端校验是否允许跳转。
  const handleReplyClick = async (item: IUserReplyItem) => {
    // 当前评论正在校验时忽略重复点击。
    if (checkingPid === item.pid) return;
    if (item.quote?.is_deleted) {
      // 上报删除态拦截失败。
      reportReplyEntryClick(item, false, t("original_comment_deleted"));
      // 展示 PC Toast 提示。
      Toast.show({
        // 使用详情页同款“原评论已删除”文案。
        content: t("original_comment_deleted"),
        // 指定 PC Toast 样式。
        isPc: true,
      });
      // 删除态不继续跳转详情页。
      return;
    }

    try {
      // 标记当前评论进入校验中。
      setCheckingPid(item.pid);
      // 请求评论跳转状态。
      const res = await checkReplyRedirectStatus(item.pid);

      // 后端允许跳转时在当前页进入详情。
      if (res?.can_redirect) {
        // 上报成功进入详情埋点。
        reportReplyEntryClick(item, true);
        window.location.href = `${pagePathUrl.detail}?tid=${item.tid}&highlightPid=${item.pid}`;
        return;
      }

      // 上报后端不允许跳转失败。
      reportReplyEntryClick(item, false, res?.message || t("original_comment_deleted"));
      // 后端不允许跳转时，展示接口返回文案。
      Toast.show({
        content: res?.message || t("original_comment_deleted"),
        isPc: true,
      });
    } catch (err: any) {
      // 上报请求失败。
      reportReplyEntryClick(item, false, err?.message || t("original_comment_deleted"));
      // 请求失败时展示后端错误信息，避免进入不可访问详情页。
      Toast.show({
        content: err?.message || t("original_comment_deleted"),
        isPc: true,
      });
    } finally {
      // 校验结束后释放点击锁。
      setCheckingPid(null);
    }
  };

  // 首次加载且无数据时展示骨架屏。
  if (loading && data.length === 0) {
    // 渲染固定数量评论骨架项。
    return (
      <div className={styles.list}>
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonReplyItem key={`reply-skeleton-${index}`} />
        ))}
      </div>
    );
  }

  // 常规渲染评论列表。
  return (
    // 列表外层容器。
    <div className={styles.list}>
      {/* 遍历我的评论数据。 */}
      {(data || []).map((item: IUserReplyItem, index) => {
        // 提取“我的回复内容”里的第一张普通图片。
        const replyImageSrc = extractFirstImageSrc(item.content);
        // 读取被引用评论的原始内容。
        const quoteContent = item.quote?.content;
        // 判断当前回复是否有关联的引用评论。
        const hasQuote = item.quote?.pid;
        // 将引用评论转成纯文本，用于图片 alt。
        const quoteText = sanitizeText(quoteContent);
        // 提取引用评论里的第一张普通图片。
        const quoteImageSrc = extractFirstImageSrc(quoteContent);
        // 清洗引用评论作者名，避免直接渲染接口 HTML。
        const quoteAuthor = sanitizeText(item.quote?.author);
        // 生成“我的回复内容”预 HTML，兼容表情包。
        const replyTitleHtml = renderPreviewHtml(item.content, emojies);
        // 生成引用评论预览 HTML，兼容表情包。
        const quotePreviewHtml = renderPreviewHtml(quoteContent, emojies);

        // 是否为最后一条评论。
        const isLastItem = index === data.length - 1;

        // 返回单条我的评论节点。
        return (
          // 单条评论点击区域。
          <div
            // 使用评论 pid 作为 React key。
            key={item.pid}
            // 应用单条评论样式，最后一条去掉下划线。
            className={cx(styles.item, { [styles.itemLast]: isLastItem })}
            // 点击整条评论时先校验后端状态，再决定跳转或提示。
            onClick={() => handleReplyClick(item)}
          >
            {/* 单条评论主体容器。 */}
            <div className={styles.main}>
              {/* 第 1 行：我回复的内容，使用安全 HTML 展示表情图片。 */}
              <div className={styles.replyTitle} dangerouslySetInnerHTML={replyTitleHtml} />

              {/* 第 2 行起：引用内容、图片、原帖标题和时间。 */}
              <div className={styles.replyTargetBlock}>
                {/* 我的回复中存在图片时，在文本下面展示第一张图片。 */}
                {!!replyImageSrc && (
                  // 我的回复图片容器。
                  <div className={styles.replyImage}>
                    {/* 我的回复图片。 */}
                    <CommonImage
                      // 图片地址来自回复内容中的第一张 img。
                      src={replyImageSrc}
                      // 图片 alt 使用纯文本回复内容兜底。
                      alt={sanitizeText(item.content) || "reply"}
                      // 图片填充容器。
                      layout="fill"
                      // 图片按 cover 裁切。
                      cutMode="cover"
                      // 声明普通图片类型。
                      type={ImageType.image}
                    />
                  </div>
                )}

                {/* 引用评论存在且未被删除时展示引用预览。 */}
                {hasQuote && !!quoteContent && (
                  // 使用 Fragment 包裹引用文本和引用图片。
                  <>
                    {/* 引用评论文本预览。 */}
                    <div className={styles.preview}>
                      {/* 引用评论作者与固定前缀。 */}
                      回复 @{quoteAuthor}：
                      {/* 引用评论正文，使用安全 HTML 展示表情图片。 */}
                      <span dangerouslySetInnerHTML={quotePreviewHtml} />
                    </div>
                    {/* 引用评论存在图片时，在引用文本下方展示第一张图片。 */}
                    {!!quoteImageSrc && (
                      // 引用评论图片容器。
                      <div className={styles.replyImage} style={{ marginTop: 8 }}>
                        {/* 引用评论图片。 */}
                        <CommonImage
                          // 图片地址来自引用评论内容中的第一张 img。
                          src={quoteImageSrc}
                          // 图片 alt 使用引用纯文本兜底。
                          alt={quoteText || "reply"}
                          // 图片填充容器。
                          layout="fill"
                          // 图片按 cover 裁切。
                          cutMode="cover"
                          // 声明普通图片类型。
                          type={ImageType.image}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* 引用评论存在但内容为空时，按删除态展示。 */}
                {hasQuote && !quoteContent && (
                  // 删除态引用评论预览。
                  <div className={styles.preview}>
                    {/* 固定回复前缀。 */}
                    回复{" "}
                    {/* 删除态文案使用灰色斜体。 */}
                    <span style={{ color: "#c8c9cb", fontStyle: "italic" }}>原评论已删除</span>
                  </div>
                )}

                {/* 第 3 行：回复所属原帖标题。 */}
                {!!item.thread_title && (
                  // 原帖标题预览，点击整条仍进入详情页。
                  <div className={styles.threadTitle}>原帖：{sanitizeText(item.thread_title)}</div>
                )}

                {/* 第 4 行：我的回复时间。 */}
                {!!item.dateline && (
                  // 使用消息列表同款相对时间格式。
                  <div className={styles.time}>{formatMessageDateByDayJs(item.dateline)}</div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* 分页加载中时展示底部 loading。 */}
      {loading && data.length > 0 && (
        // 分页 loading 容器。
        <div className={styles.paginationLoading}>
          {/* loading 转圈图形。 */}
          <span className={styles.loadingDot} />
          {/* loading 文案。 */}
          <span>{t("user_center_loading")}</span>
        </div>
      )}
      {/* 无更多数据时的底部提示文案 */}
      {!loading && data.length > 0 && !hasMore && noMoreText && (
        <div className={styles.noMoreText}>{noMoreText}</div>
      )}
    </div>
  );
};

// 导出我的评论列表组件。
export default UserReplyList;

